import { API_BASE_URL } from "../config";
import { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Button,
} from "@mui/material";

import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";

function AddModal({
  open,
  onClose,
  onUserAdded,
  isProject = false,
  isComment = false,
  isTask = false,
  editData = null,
  refreshNotifications,
}) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [email, setEmail] = useState("");
  const [selectedRole, setSelectedRole] = useState("user");
  const [password, setPassword] = useState("");
  const [projects, setProjects] = useState([]);
  const [status, setStatus] = useState("To Do");
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [commentText, setCommentText] = useState("");
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [estimatedMinutes, setEstimatedMinutes] = useState("");
  const [projectNumber, setProjectNumber] = useState("");

  const [users, setUsers] = useState([]);
  const [assignedUserId, setAssignedUserId] = useState("");

  const [showPassword, setShowPassword] = useState(false);
  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const [emailError, setEmailError] = useState("");

  const roleOptions = [
    { value: "user", label: "User" },
    { value: "admin", label: "Admin" },
  ];

  const userRole = localStorage.getItem("role");

  useEffect(() => {
    setProjects([]);
    setUsers([]);
    if (open && isTask && editData) {
      setName(editData.title || "");
      setDescription(editData.description || "");
      setStatus(
        ["To Do", "In Progress", "Completed"].includes(editData.status)
          ? editData.status
          : "To Do"
      );
      setSelectedProjectId(
        editData.project_id ? String(editData.project_id) : ""
      );
      setAssignedUserId(
        editData.assigned_user_id ? String(editData.assigned_user_id) : ""
      );
    } else if (editData) {
      setName(editData.name || "");
      setDescription(editData.description || "");
      setEmail(editData.email || "");
      setSelectedRole(editData.role || "user");
      setSelectedProjects(
        editData.projectIds ? editData.projectIds.map(String) : []
      );
      setStatus(editData.status || "active");
      setEstimatedMinutes(editData.estimated_minutes || "");
      if (isComment) {
        setCommentText(editData.text || "");
        setSelectedProjectId(
          editData.project_id ? String(editData.project_id) : ""
        );
      }
      // Fetch assigned projects for user when editing a user
      if (!isProject && !isTask && !isComment && editData?.id) {
        fetch(`${API_BASE_URL}/api/admin/users/${editData.id}/projects`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          credentials: "include",
        })
          .then((res) => res.json())
          .then((data) => {
            setSelectedProjects(data.map((p) => String(p.id)));
          })
          .catch((err) =>
            console.error("Failed to fetch assigned projects for edit", err)
          );
      }
    } else {
      setName("");
      setDescription("");
      setEmail("");
      setSelectedRole("user");
      setPassword("");
      setSelectedProjects([]);
      setCommentText("");
      setSelectedProjectId("");
      setEstimatedMinutes("");
      setAssignedUserId("");
      setProjectNumber("");
    }
  }, [editData, open, isTask]);

  useEffect(() => {
    if (isTask || !isProject) {
      let endpoint = "";
      if (userRole === "user") {
        endpoint = "/api/user/projects/assigned";
      } else {
        endpoint = "/api/admin/projects";
      }
      fetch(API_BASE_URL + endpoint, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        credentials: "include",
      })
        .then((res) => res.json())
        .then((data) => {
          setProjects(Array.isArray(data) ? data : []);
        })
        .catch((err) => console.error("Failed to fetch projects", err));
    }
  }, [open, isProject, isTask]);

  useEffect(() => {
    if (isTask) {
      // Only fetch users if admin, as /api/user/users route may not exist
      if (userRole === "admin") {
        fetch(API_BASE_URL + `/api/admin/users`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          credentials: "include",
        })
          .then((res) => res.json())
          .then((data) => {
            setUsers(Array.isArray(data) ? data : []);
          })
          .catch((err) => console.error("Failed to fetch users", err));
      }
    }
  }, [open, isTask]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isComment) {
      if (projects.length === 0) {
        alert("No projects available. Cannot add comment.");
        return;
      }
    }

    const token = localStorage.getItem("token");

    let endpoint = "";
    let payload = {};

    if (isComment) {
      if (!selectedProjectId || !commentText.trim()) {
        alert("Please enter comment and select a project.");
        return;
      }

      endpoint = `/api/${userRole}/comments/project/${selectedProjectId}`;
      payload = {
        comment: commentText,
        projectId: selectedProjectId,
      };
    } else if (isProject) {
      endpoint = editData
        ? `/api/admin/projects/${editData.id}`
        : "/api/admin/projects";
      payload = {
        name,
        description,
        status,
        project_number: projectNumber,
        estimated_minutes: estimatedMinutes,
      };
    } else if (isTask) {
      if (!selectedProjectId || !name.trim() || !description.trim()) {
        alert(
          "Please enter all required task fields (title, description, and project)."
        );
        return;
      }
      endpoint =
        userRole === "admin"
          ? `/api/admin/tasks/${editData ? editData.id : ""}`
          : `/api/tasks/${editData ? editData.id : ""}`;
      payload = {
        title: name.trim(),
        description: description.trim(),
        status,
        project_id: selectedProjectId,
        assigned_user_id: assignedUserId || null,
      };
    } else {
      endpoint = editData
        ? `/api/admin/users/${editData.id}`
        : "/api/admin/users";
      payload = editData
        ? {
            name,
            email,
            role: selectedRole,
            status,
            projectIds: selectedProjects,
          }
        : {
            name,
            email,
            role: selectedRole,
            password,
            projectIds: selectedProjects,
          };

      // Check for duplicate email before user creation
      if (!editData) {
        // Proveri da li email već postoji
        const checkRes = await fetch(
          `${API_BASE_URL}/api/admin/users/check-email`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ email }),
          }
        );

        const checkData = await checkRes.json();
        if (!checkRes.ok) {
          alert(checkData.error || "Error checking email.");
          return;
        }
        if (checkData.exists) {
          setEmailError("User with this email already exists.");
          return;
        } else {
          setEmailError(""); // clear any previous error
        }
      }
    }

    try {
      const res = await fetch(API_BASE_URL + endpoint, {
        method: editData ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
        credentials: "include",
      });

      if (res.ok) {
        refreshNotifications?.();
        onUserAdded();
        onClose();
      } else {
        const error = await res.json();
        alert(error.error || "An error occurred");
      }
    } catch (err) {
      console.error("Submit error:", err);
      alert("Something went wrong while submitting.");
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {editData
          ? `Edit ${
              isProject
                ? "Project"
                : isTask
                ? "Task"
                : isComment
                ? "Comment"
                : "User"
            }`
          : `Add New ${
              isProject
                ? "Project"
                : isTask
                ? "Task"
                : isComment
                ? "Comment"
                : "User"
            }`}
      </DialogTitle>

      <DialogContent dividers>
        <form onSubmit={handleSubmit} id="add-form">
          {isTask ? (
            <>
              <TextField
                fullWidth
                label="Title"
                variant="outlined"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                variant="outlined"
                sx={{ mb: 2 }}
              />
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Select Project</InputLabel>
                <Select
                  value={selectedProjectId}
                  onChange={(e) => setSelectedProjectId(String(e.target.value))}
                  input={<OutlinedInput label="Select Project" />}
                  renderValue={(selected) => {
                    const project = projects.find(
                      (p) => String(p.id) === String(selected)
                    );
                    return project ? project.name : "";
                  }}
                >
                  {projects.length > 0 ? (
                    projects.map((proj) => (
                      <MenuItem key={proj.id} value={String(proj.id)}>
                        {proj.name}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>No projects available</MenuItem>
                  )}
                </Select>
              </FormControl>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Select User</InputLabel>
                <Select
                  value={assignedUserId}
                  onChange={(e) => setAssignedUserId(String(e.target.value))}
                  input={<OutlinedInput label="Select User" />}
                  renderValue={(selected) => {
                    const user = users.find(
                      (u) => String(u.id) === String(selected)
                    );
                    return user ? `${user.name} (${user.email})` : "";
                  }}
                >
                  {users.length > 0 ? (
                    users.map((user) => (
                      <MenuItem key={user.id} value={String(user.id)}>
                        {user.name} ({user.email})
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>No users available</MenuItem>
                  )}
                </Select>
              </FormControl>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={status || ""}
                  onChange={(e) => setStatus(e.target.value)}
                  input={<OutlinedInput label="Status" />}
                >
                  <MenuItem value="To Do">To Do</MenuItem>
                  <MenuItem value="In Progress">In Progress</MenuItem>
                  <MenuItem value="Completed">Completed</MenuItem>
                </Select>
              </FormControl>
            </>
          ) : isComment ? (
            <>
              <TextField
                fullWidth
                label="Comment"
                variant="outlined"
                multiline
                rows={4}
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                required
                sx={{ mb: 2 }}
              />
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Select Project</InputLabel>
                <Select
                  value={selectedProjectId}
                  onChange={(e) => setSelectedProjectId(e.target.value)}
                  input={<OutlinedInput label="Select Project" />}
                >
                  {Array.isArray(projects) &&
                    projects.map((proj) => (
                      <MenuItem key={proj.id} value={proj.id}>
                        {proj.name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </>
          ) : isProject ? (
            <>
              <TextField
                fullWidth
                label="Name"
                variant="outlined"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label="Project Number"
                type="number"
                variant="outlined"
                value={projectNumber}
                onChange={(e) => setProjectNumber(e.target.value)}
                required
                sx={{
                  mb: 2,
                }}
              />
              <TextField
                fullWidth
                label="Estimated Time (minutes)"
                type="number"
                variant="outlined"
                value={estimatedMinutes}
                onChange={(e) => setEstimatedMinutes(e.target.value)}
                required
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                variant="outlined"
                sx={{ mb: 2 }}
              />
            </>
          ) : (
            <>
              <TextField
                fullWidth
                label="Name"
                variant="outlined"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Email"
                type="email"
                variant="outlined"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                sx={{ mb: 2 }}
                error={!!emailError}
                helperText={emailError}
              />

              {!editData && (
                <TextField
                  fullWidth
                  label="Password"
                  type={showPassword ? "text" : "password"}
                  variant="outlined"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  sx={{ mb: 2 }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={togglePasswordVisibility}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Role</InputLabel>
                <Select
                  value={selectedRole}
                  onChange={(e) => setSelectedRole(e.target.value)}
                  label="Role"
                >
                  {roleOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Assign Projects</InputLabel>
                <Select
                  multiple
                  value={selectedProjects.map(String)}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSelectedProjects(
                      typeof value === "string"
                        ? value.split(",")
                        : value.map(String)
                    );
                  }}
                  input={<OutlinedInput label="Assign Projects" />}
                  renderValue={(selected) =>
                    selected.length === 0
                      ? "Assign Projects"
                      : projects
                          .filter((p) => selected.includes(String(p.id)))
                          .map((p) => p.name)
                          .join(", ")
                  }
                >
                  {projects.length > 0 ? (
                    projects.map((proj) => (
                      <MenuItem key={proj.id} value={String(proj.id)}>
                        <Checkbox
                          checked={selectedProjects
                            .map(String)
                            .includes(String(proj.id))}
                        />
                        <ListItemText primary={proj.name} />
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>No projects available</MenuItem>
                  )}
                </Select>
              </FormControl>
            </>
          )}
        </form>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          type="submit"
          variant="contained"
          form="add-form"
          style={{
            background: "var(--gradient-color)",
            color: "#fff",
            borderRadius: 20,
          }}
        >
          {editData ? "Save" : "Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AddModal;
